import type { ValidationRule } from "@/domains/buildings/types/buildingWidget";
import type { EnumOption } from "./EnumMultiSelect";

/**
 * Resolve enum / enum_multiselect rules into a `{ kind, options }` pair the
 * dropdown can render directly. `value` is the canonical English string we
 * persist; `label` is the localized variant shown in the menu.
 *
 * Returns `null` when the attribute has no enum-typed validation rules.
 */
export function getEnumRule(
  validation: ValidationRule[] | undefined,
  language: "en" | "et"
): { kind: "enum" | "enum_multiselect"; options: EnumOption[] } | null {
  if (!validation || validation.length === 0) return null;
  const enumRules = validation.filter(
    (r) => r.validation_type === "enum" || r.validation_type === "enum_multiselect"
  );
  if (enumRules.length === 0) return null;
  const kind = enumRules[0].validation_type as "enum" | "enum_multiselect";
  const options = [...enumRules]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map<EnumOption>((r) => ({
      value: r.validation_value,
      label:
        language === "et" && r.validation_value_et ? r.validation_value_et : r.validation_value,
    }));
  return { kind, options };
}

/**
 * Resolve a canonical (English) stored value to its locale-aware label using an
 * enum rule. Handles comma-joined multi-select values segment-by-segment and
 * falls back to the raw value when no option matches (extractor / user free-form
 * values). Returns the value unchanged when there is no enum rule. Shared by the
 * field row and the value-history modal (WHA-3720).
 */
export function localizeEnumValue(value: string, enumRule: ReturnType<typeof getEnumRule>): string {
  if (!enumRule || !value) return value;
  // Matched the same trim + case-insensitive way `isSameEvidenceValue` groups
  // values, because the evidence panel feeds this extractor and registry
  // strings: an option is "Proprietary" while a source reports "proprietary "
  // or "Proprietary ". Strict equality left exactly those rows falling back to
  // the raw English under an Estonian Value dropdown — the bug this helper
  // exists to fix, unfixed for the case the panel hits most.
  const labelFor = (raw: string) => {
    const key = raw.trim().toLowerCase();
    return enumRule.options.find((o) => o.value.trim().toLowerCase() === key)?.label ?? raw;
  };
  if (enumRule.kind === "enum_multiselect") {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .map(labelFor)
      .join(", ");
  }
  return labelFor(value);
}
